import { ArrowLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import cn from "classnames";
import { ConfirmationDialog, NumberStepper, Section } from "@hicya/ui";

import { HabitFormLoading } from "@/components/layout/route-loading";
import { CategoryPicker } from "@/components/form/category-picker";
import { ColorPicker } from "@/components/form/color-picker";
import { IconPicker } from "@/components/form/icon-picker";
import { ScheduleConfig } from "@/components/form/schedule-config";
import { useHabits } from "@/context/habits-context";
import { useDatabase } from "@/db/provider";

import styles from "./habit-edit.module.scss";

import type { Habit, HabitType, ScheduleType, TimeOfDay } from "@hicya/habits-shared/db";

const TIME_OPTIONS: Array<{ value: TimeOfDay; label: string }> = [
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "evening", label: "Evening" },
  { value: "anytime", label: "Anytime" },
];

function buildScheduleValue(
  type: ScheduleType,
  days: number[],
  count: number,
  interval: number,
): string {
  switch (type) {
    case "daily":
      return "{}";
    case "specific_days":
      return JSON.stringify({ days });
    case "x_per_week":
    case "x_per_month":
      return JSON.stringify({ count });
    case "every_n_days":
      return JSON.stringify({ interval });
  }
}

function parseScheduleFields(habit: Habit): { days: number[]; count: number; interval: number } {
  try {
    const parsed = JSON.parse(habit.schedule_value) as Record<string, unknown>;
    return {
      days: (parsed.days as number[]) ?? [1, 2, 3, 4, 5],
      count: (parsed.count as number) ?? 3,
      interval: (parsed.interval as number) ?? 2,
    };
  } catch {
    return { days: [1, 2, 3, 4, 5], count: 3, interval: 2 };
  }
}

export function HabitEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state, updateHabit, archiveHabit, deleteHabit, createCategory } = useHabits();
  const db = useDatabase();

  const [habit, setHabit] = useState<Habit | null>(null);
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("");
  const [color, setColor] = useState("");
  const [habitType, setHabitType] = useState<HabitType>("boolean");
  const [targetCount, setTargetCount] = useState(1);
  const [scheduleType, setScheduleType] = useState<ScheduleType>("daily");
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [count, setCount] = useState(3);
  const [interval, setInterval] = useState(2);
  const [timeOfDay, setTimeOfDay] = useState<TimeOfDay>("anytime");
  const [showStreak, setShowStreak] = useState(true);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);

  // Load habit
  useEffect(() => {
    if (!id) return;
    let disposed = false;
    setLoadError(false);
    void db
      .getHabitById(id)
      .then((h) => {
        if (disposed) return;
        if (!h) {
          void navigate("/today");
          return;
        }
        setHabit(h);
        setName(h.name);
        setIcon(h.icon);
        setColor(h.color);
        setHabitType(h.type);
        setTargetCount(h.target_count);
        setScheduleType(h.schedule_type);
        setTimeOfDay(h.time_of_day);
        setShowStreak(Boolean(h.show_streak));
        setCategoryId(h.category_id);
        const fields = parseScheduleFields(h);
        setDays(fields.days);
        setCount(fields.count);
        setInterval(fields.interval);
      })
      .catch(() => {
        if (!disposed) setLoadError(true);
      });
    return () => {
      disposed = true;
    };
  }, [id, db, navigate, loadAttempt]);

  const handleSave = useCallback(async () => {
    if (!id || !name.trim() || submitting) return;
    setSubmitting(true);
    try {
      await updateHabit(id, {
        name: name.trim(),
        icon,
        color,
        type: habitType,
        target_count: habitType === "count" ? targetCount : 1,
        schedule_type: scheduleType,
        schedule_value: buildScheduleValue(scheduleType, days, count, interval),
        time_of_day: timeOfDay,
        show_streak: showStreak,
        category_id: categoryId,
      });
      void navigate("/today");
    } finally {
      setSubmitting(false);
    }
  }, [
    id,
    name,
    submitting,
    updateHabit,
    icon,
    color,
    habitType,
    targetCount,
    scheduleType,
    days,
    count,
    interval,
    timeOfDay,
    showStreak,
    categoryId,
    navigate,
  ]);

  const handleArchive = useCallback(async () => {
    if (!id) return;
    await archiveHabit(id);
    void navigate("/today");
  }, [id, archiveHabit, navigate]);

  const handleDelete = useCallback(async () => {
    if (!id) return;
    await deleteHabit(id);
    void navigate("/today");
  }, [id, deleteHabit, navigate]);

  if (loadError)
    return (
      <div className={styles.page}>
        <h2 className={styles.heading}>Edit Habit</h2>
        <div role="alert">
          <p>This habit couldn’t be loaded.</p>
          <button type="button" onClick={() => setLoadAttempt((value) => value + 1)}>
            Try again
          </button>
        </div>
      </div>
    );

  if (!habit) {
    return <HabitFormLoading />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <button type="button" className={styles.back} onClick={() => navigate(-1)}>
          <ArrowLeftIcon size={20} aria-hidden="true" /> Back
        </button>
      </div>

      <h2 className={styles.heading}>Edit Habit</h2>

      <div className={styles.form}>
        <Section title="Basics">
          <input
            type="text"
            className={styles.input}
            placeholder="Habit name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            aria-label="Habit name"
            data-testid="habit-name-input"
          />
          <IconPicker value={icon} onChange={setIcon} />
          <ColorPicker value={color} onChange={setColor} />
        </Section>

        {state.categories.length > 0 && (
          <Section title="Category">
            <CategoryPicker
              categories={state.categories}
              value={categoryId}
              onChange={setCategoryId}
              onCreateCategory={(cat) => void createCategory(cat)}
            />
          </Section>
        )}

        <Section title="Type & Target">
          <div className={styles.typeToggle} role="radiogroup" aria-label="Habit type">
            <button
              type="button"
              className={cn(styles.typeOption, habitType === "boolean" && styles.active)}
              onClick={() => setHabitType("boolean")}
              role="radio"
              aria-checked={habitType === "boolean"}
            >
              Yes/No
            </button>
            <button
              type="button"
              className={cn(styles.typeOption, habitType === "count" && styles.active)}
              onClick={() => setHabitType("count")}
              role="radio"
              aria-checked={habitType === "count"}
            >
              Count
            </button>
          </div>
          {habitType === "count" && (
            <NumberStepper
              value={targetCount}
              onChange={setTargetCount}
              min={1}
              max={100}
              label="Target count"
            />
          )}
        </Section>

        <Section title="Schedule">
          <ScheduleConfig
            scheduleType={scheduleType}
            onScheduleTypeChange={setScheduleType}
            days={days}
            onDaysChange={setDays}
            count={count}
            onCountChange={setCount}
            interval={interval}
            onIntervalChange={setInterval}
          />
        </Section>

        <Section title="Time of Day">
          <div className={styles.timeOptions} role="radiogroup" aria-label="Time of day">
            {TIME_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={cn(styles.timeCard, timeOfDay === opt.value && styles.active)}
                onClick={() => setTimeOfDay(opt.value)}
                role="radio"
                aria-checked={timeOfDay === opt.value}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Section>

        <Section title="Advanced" collapsible defaultExpanded={false}>
          <label className={styles.toggleRow}>
            <span>Show streak</span>
            <input
              type="checkbox"
              checked={showStreak}
              onChange={(e) => setShowStreak(e.target.checked)}
            />
          </label>
        </Section>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.statsLink}
          onClick={() => void navigate(`/habits/${id}/stats`)}
          data-testid="view-stats-button"
        >
          View Stats
        </button>
        <button
          type="button"
          className={styles.archiveButton}
          onClick={() => void handleArchive()}
          data-testid="archive-habit-button"
        >
          Archive Habit
        </button>
        <button
          type="button"
          className={styles.deleteButton}
          onClick={() => setShowDeleteConfirm(true)}
          data-testid="delete-habit-button"
        >
          Delete Habit
        </button>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.submit}
          onClick={() => void handleSave()}
          disabled={!name.trim() || submitting}
          data-testid="save-habit-button"
        >
          {submitting ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {showDeleteConfirm && (
        <ConfirmationDialog
          title="Delete Habit"
          message="This will remove the habit and all its history. This cannot be undone."
          confirmLabel="Delete"
          destructive
          onConfirm={() => void handleDelete()}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
}
